<template>
  <div
    class="availability-card"
    ref="cardRef"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    @mouseenter="handleMouseEnter"
  >
    <!-- LAYER 1: The UI (Facade) -->
    <!-- Always visible, but might be dimmed slightly when scanning -->
    <div class="layer ui-layer">
      <div class="availability-card__content">
        <div class="status-indicator">
          <span class="status-dot">
            <span class="status-dot-ping"></span>
          </span>
          <span class="status-text">{{ $t('page.about.availabilityCard.status') }}</span>
        </div>
        <p class="status-message">{{ $t('page.about.availabilityCard.message') }}</p>
      </div>
    </div>

    <!-- LAYER 2: The Code (Structure) - Desktop -->
    <!-- Revealed by mask -->
    <div class="layer code-layer" :style="codeLayerStyle" :class="{ 'light-theme': !isDark }">
      <div class="code-container">
        <!-- Line Numbers -->
        <div class="line-numbers">
          <span v-for="n in codeLines.length" :key="n">{{ n }}</span>
        </div>
        <!-- Code Content -->
        <div class="code-content">
          <div v-for="(line, i) in codeLines" :key="i" class="code-line" v-html="line"></div>
        </div>
      </div>
    </div>

    <!-- Scanner Beam Visual -->
    <div class="scanner-beam" :style="beamStyle"></div>

    <!-- Mobile Scan Button -->
    <button class="availability-card__mobile-scan" @click="openMobileModal" title="Scan Code">
      <ViewIcon />
    </button>

    <!-- Mobile Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="isMobileModalOpen" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" @click="closeMobileModal">
          <div class="relative w-full max-w-md p-6 rounded-2xl border border-white/10 shadow-2xl overflow-hidden" :class="isDark ? 'bg-[#262a33]' : 'bg-[#fafafa]'" @click.stop>
            
            <!-- Close Button -->
            <button class="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-20" :class="isDark ? 'text-white' : 'text-black'" @click="closeMobileModal">
              <Cancel01Icon size="20" />
            </button>

            <!-- Code Content (Typed) -->
            <div class="relative z-10 flex gap-4 font-mono text-xs sm:text-sm leading-relaxed" :class="isDark ? 'text-[#e5e5e5]' : 'text-[#383a42]'">
               <!-- Line Numbers -->
               <div class="line-numbers select-none text-right min-w-[1.5em]">
                  <div v-for="n in codeLines.length" :key="n" class="leading-relaxed">{{ n }}</div>
               </div>
               <!-- Typed Code -->
               <div class="code-content w-full">
                  <div v-for="(line, i) in typedLines" :key="i" class="code-line min-h-[1.5em]" v-html="line"></div>
               </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { ViewIcon, Cancel01Icon } from 'hugeicons-vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { isDark } = useTheme()
const cardRef = ref(null)
const mouse = ref({ x: -100, y: -100 })
const isHovering = ref(false)
const isMobileScanning = ref(false)
const isMobileModalOpen = ref(false)

// Static Code Lines (Computed to react to language changes)
const codeLines = computed(() => [
  `<span class="key">"status"</span>: <span class="string">"${t('page.about.availabilityCard.status')}"</span>,`,
  `<span class="key">"mode"</span>: <span class="string">"OPEN_TO_BUILD"</span>,`,
  `<span class="key">"focus"</span>: [<span class="string">"FRONTEND"</span>, <span class="string">"UI/UX"</span>],`,
  `<span class="key">"rate"</span>: <span class="string">"let's talk"</span>,`,
  `<span class="key">"water"</span>: <span class="boolean">true</span>,`,
  `<span class="key">"location"</span>: <span class="null">"Remote"</span>`
])

// Mobile Typing Logic
const typedLines = ref([])
let typingTimeouts = [] // Not strictly needed with await, but good for consistency if we had global timeouts

const typeHtml = async (html, lineIndex) => {
  // Simple HTML parser for typing effect
  const regex = /<[^>]*>|[^<]+/g
  const matches = html.match(regex) || []
  
  let currentHtml = ''
  
  for (const part of matches) {
    if (!isMobileModalOpen.value) { // Stop typing if modal is closed
      typedLines.value[lineIndex] = '' // Clear current line if interrupted
      return
    }
    if (part.startsWith('<')) {
      // It's a tag, append immediately
      currentHtml += part
      typedLines.value[lineIndex] = currentHtml
    } else {
      // It's text, type char by char
      for (const char of part) {
        if (!isMobileModalOpen.value) { // Stop typing if modal is closed
          typedLines.value[lineIndex] = '' // Clear current line if interrupted
          return
        }
        currentHtml += char
        typedLines.value[lineIndex] = currentHtml
        await new Promise(resolve => setTimeout(resolve, 20 + Math.random() * 30)) // Typing speed
      }
    }
  }
}

const startTyping = async () => {
  // Reset
  typedLines.value = new Array(codeLines.value.length).fill('')
  
  // Type each line sequentially
  for (let i = 0; i < codeLines.value.length; i++) {
    if (!isMobileModalOpen.value) break // Stop if modal was closed during typing
    await typeHtml(codeLines.value[i], i)
  }
}

const openMobileModal = () => {
  isMobileModalOpen.value = true
  startTyping()
}

const closeMobileModal = () => {
  isMobileModalOpen.value = false
  // Optionally clear typedLines immediately or let it fade out
  typedLines.value = [] 
}

// Mouse Interaction
const handleMouseMove = (e) => {
  if (isMobileScanning.value) return

  if (!cardRef.value) return
  const rect = cardRef.value.getBoundingClientRect()
  mouse.value = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  }
}

const handleMouseEnter = () => {
  isHovering.value = true
}

const handleMouseLeave = () => {
  isHovering.value = false
}

// Controls the revealing of the code layer
const codeLayerStyle = computed(() => {
  const x = mouse.value.x
  const y = mouse.value.y

  return {
    opacity: isHovering.value ? 1 : 0,
    maskImage: `radial-gradient(circle at ${x}px ${y}px, black 0%, black 80px, transparent 150px)`,
    WebkitMaskImage: `radial-gradient(circle at ${x}px ${y}px, black 0%, black 80px, transparent 150px)`,
  }
})

// Visual ring following mouse
const beamStyle = computed(() => {
  return {
    opacity: isHovering.value ? 1 : 0,
    transform: `translate(${mouse.value.x}px, ${mouse.value.y}px)`,
  }
})

// Deprecated in-place toggle, but kept if ever needed (not used now)
const toggleMobileScan = () => {
  isMobileScanning.value = !isMobileScanning.value
  isHovering.value = isMobileScanning.value

  if (isMobileScanning.value && cardRef.value) {
    const rect = cardRef.value.getBoundingClientRect()
    mouse.value = {
      x: rect.width / 2,
      y: rect.height / 2,
    }
  } else {
    mouse.value = { x: -100, y: -100 }
    isHovering.value = false
  }
}
</script>

<style scoped>
.availability-card {
  @apply relative w-full h-full overflow-hidden rounded-3xl cursor-crosshair;
  background: var(--color-surface);
}

.layer {
  @apply absolute inset-0 w-full h-full;
}

/* --- UI Layer Styles --- */
.ui-layer {
  @apply flex flex-col items-center justify-center p-6 text-center;
  z-index: 1;
}

.fluid-background {
  @apply absolute inset-0 overflow-hidden opacity-40;
}

.availability-card__content {
  @apply relative z-10 flex flex-col items-center;
}

.status-indicator {
  @apply flex items-center gap-2 mb-2 px-3 py-1.5 rounded-full;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

.status-dot {
  @apply relative w-2 h-2 rounded-full bg-emerald-400;
  box-shadow: 0 0 10px rgba(52, 211, 153, 0.8);
}

.status-dot-ping {
  @apply absolute inset-0 rounded-full bg-emerald-400 opacity-75;
  animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.status-text {
  @apply text-xs font-bold uppercase tracking-widest text-emerald-400;
}

.status-message {
  @apply text-lg font-medium text-[var(--color-text-primary)];
}

/* --- Code Layer Styles --- */
.code-layer {
  @apply flex items-center justify-center p-6 font-mono text-sm leading-relaxed overflow-hidden;
  background-color: #262a33; /* Andromeda Dark Background Default */
  color: #e5e5e5; /* Andromeda Dark Foreground Default */
  z-index: 2; /* On top of UI */
  pointer-events: none; /* Let clicks pass through */
  transition:
    background-color 0.3s ease,
    opacity 0.3s ease;
}

.code-layer.light-theme {
  background-color: #fafafa; /* Light Background */
  color: #383a42; /* Light Foreground */
}

.code-container {
  @apply flex gap-4 w-full h-full justify-center items-center;
}

.line-numbers {
  @apply flex flex-col text-right select-none opacity-40 font-mono text-xs sm:text-sm;
  color: inherit; /* Inherit from parent .code-layer */
  min-width: 1.5em; 
}

.code-content {
  @apply flex flex-col text-left;
}

/* Color definitions moved to .code-layer to affect line numbers too */


.code-line {
  min-height: 1.5em; /* Ensure empty lines keep height for consistent line numbering */
  white-space: pre-wrap; /* Allow wrapping if needed, or pre */
}

/* Syntax Highlighting Mock - Andromeda Theme */
/* Dark Mode Defaults */
/* Using :deep() to ensure these styles apply to content rendered via v-html */
:deep(.key) {
  color: #0fa8cd;
} /* Cyan */
:deep(.string) {
  color: #05bc79;
} /* Green */
:deep(.number) {
  color: #e5e512;
} /* Yellow */
:deep(.boolean) {
  color: #bc3fbc;
} /* Purple */
:deep(.null) {
  color: #cd3131;
} /* Red */

/* Light Mode Overrides (Darker/High Contrast versions of Andromeda colors) */
.light-theme :deep(.key) {
  color: #007a99;
} /* Dark Cyan */
.light-theme :deep(.string) {
  color: #048c5b;
} /* Dark Green */
.light-theme :deep(.number) {
  color: #9c9c00;
} /* Dark Yellow */
.light-theme :deep(.boolean) {
  color: #9e259e;
} /* Dark Purple */
.light-theme :deep(.null) {
  color: #b31d1d;
} /* Dark Red */

/* Grid Overlay REMOVED */

/* --- Scanner Beam --- */
.scanner-beam {
  @apply absolute w-[160px] h-[160px] rounded-full pointer-events-none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 0 20px rgba(163, 230, 53, 0.2),
    inset 0 0 20px rgba(163, 230, 53, 0.1);
  top: -80px; /* Center anchor */
  left: -80px;
  z-index: 3;
  transition: opacity 0.2s ease;
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
}

.availability-card__mobile-scan {
  @apply absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 lg:hidden;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  color: var(--color-text-primary);
  cursor: pointer;
  opacity: 0.8;
  z-index: 20;
}

.availability-card__mobile-scan:active,
.availability-card__mobile-scan:hover {
  transform: scale(1.1);
  opacity: 1;
  border-color: #05bc79;
  color: #05bc79;
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
